/**
 * API client for OrderBatchEarmark — dedicated-batch reservations.
 *
 * All endpoints are ADMIN / OPERATIONS only (enforced on the backend).
 * Responses do not include margin data. Purchase-cost fields are present only
 * for users allowed to see supplier purchase prices.
 */

import api from '../../../services/api';
import type { QuantityUnit } from '../../../utils/commercialQuantity';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EarmarkStatus = 'ACTIVE' | 'FULFILLED' | 'RELEASED';

export interface OrderEarmark {
  id: string;
  orderId: string;
  inventoryBatchId: string;
  batchNumber: string;
  purchaseDate: string | null;
  unitCostPLN?: number | null;
  productVariantId: string;
  variantSku: string;
  variantName: string;
  quantity: number;
  unit?: QuantityUnit;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  consumedQuantity: number;
  consumedQuantityPcs?: number | null;
  consumedQuantityM2?: number | null;
  consumedQuantityM3?: number | null;
  status: EarmarkStatus;
  /** When true, the order is fulfilled for this variant ONLY from pinned batches (no FIFO). */
  isExclusive: boolean;
  earmarkedAt: string;
  fulfilledAt: string | null;
  releasedAt: string | null;
  quantityRemaining: number | null;
}

export interface CreateEarmarkPayload {
  inventoryBatchId: string;
  quantity: number;
  unit?: QuantityUnit;
  width?: number | null;
  length?: number | null;
  height?: number | null;
  thickness?: number | null;
}

export interface CreatedEarmark {
  id: string;
  orderId: string;
  inventoryBatchId: string;
  quantity: number;
  unit?: QuantityUnit;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  consumedQuantity: number;
  consumedQuantityPcs?: number | null;
  consumedQuantityM2?: number | null;
  consumedQuantityM3?: number | null;
  status: EarmarkStatus;
  earmarkedAt: string;
  freelyAvailableAfter: number;
}

export interface BatchEarmark {
  id: string;
  orderId: string;
  orderNumber: string;
  inventoryBatchId: string;
  quantity: number;
  unit?: QuantityUnit;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  consumedQuantity: number;
  consumedQuantityPcs?: number | null;
  consumedQuantityM2?: number | null;
  consumedQuantityM3?: number | null;
  status: EarmarkStatus;
  earmarkedAt: string;
  fulfilledAt: string | null;
  releasedAt: string | null;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Create (pin) an earmark for an order.
 */
export async function createEarmark(
  orderId: string,
  payload: CreateEarmarkPayload
): Promise<CreatedEarmark> {
  const response = await api.post<CreatedEarmark>(
    `/orders/${orderId}/earmarks`,
    payload
  );
  return response.data;
}

/**
 * List all earmarks for an order (all statuses).
 */
export async function fetchOrderEarmarks(orderId: string): Promise<OrderEarmark[]> {
  const response = await api.get<OrderEarmark[]>(`/orders/${orderId}/earmarks`);
  return response.data;
}

/**
 * Release (unpin) a single earmark.
 */
export async function releaseEarmark(
  orderId: string,
  earmarkId: string
): Promise<OrderEarmark> {
  const response = await api.delete<OrderEarmark>(
    `/orders/${orderId}/earmarks/${earmarkId}`
  );
  return response.data;
}

/**
 * Release every ACTIVE earmark for an order in one action.
 *
 * The backend already releases surplus earmarks automatically once the
 * order reaches a terminal status (DELIVERED/CANCELLED) — this endpoint
 * covers the earlier case: the operator decides a short-shipped order will
 * not receive another WZ and wants the still-reserved stock back in the
 * general pool right away, without waiting for (or forcing) that terminal
 * transition. See backend/src/routes/orders.js and AGENTS.md.
 */
export async function releaseAllEarmarks(orderId: string): Promise<OrderEarmark[]> {
  const response = await api.post<OrderEarmark[]>(
    `/orders/${orderId}/earmarks/release-all`
  );
  return response.data;
}

/**
 * Toggle the "exclusive source" flag on an ACTIVE earmark ("ship whole order
 * from this batch"). When true, WZ release fulfils this variant ONLY from pinned
 * batches — no general FIFO fallback to older batches.
 */
export async function setEarmarkExclusive(
  orderId: string,
  earmarkId: string,
  isExclusive: boolean
): Promise<OrderEarmark> {
  const response = await api.patch<OrderEarmark>(
    `/orders/${orderId}/earmarks/${earmarkId}/exclusive`,
    { isExclusive }
  );
  return response.data;
}

/**
 * List earmarks by batch — for the inventory batch detail "reserved for orders" view.
 *
 * Consumed by InventoryDashboard.tsx — the batch-row expansion renders a
 * "Zarezerwowane" sub-table that calls this endpoint per batch row.
 */
export async function fetchBatchEarmarks(batchId: string): Promise<BatchEarmark[]> {
  const response = await api.get<BatchEarmark[]>(
    `/inventory/batches/${batchId}/earmarks`
  );
  return response.data;
}
